import React, { useState, useEffect } from 'react'
import {
  Modal,
  Button,
  Spinner,
} from 'react-bootstrap'
import { supabase } from '../../supabaseClient'
import '../../assets/css/checklist.css'
import { DateFormtter } from '../../utils'

// sectionType: "tasks" | "lists" | "notes" | "grocery"
const SECTION_CONFIG = {
  tasks: { table: 'tasks', catTable: 'categories', catField: 'category_id', title: 'Share Tasks', hasDate: true },
  lists: { table: 'lists', catTable: 'lists_categories', catField: 'category_id', title: 'Share Lists', hasDate: false },
  notes: { table: 'notes', catTable: 'notes_categories', catField: 'category_id', title: 'Share Notes', hasDate: false },
  grocery: { table: 'grocery_table', catTable: 'grocery_categories', catField: 'grocery_table_id', title: 'Share Grocery List', hasDate: false },
}

const ShareSectionPopup = ({ isDarkMode, show, handleClose, UserId, sectionType = 'tasks' }) => {
  const config = SECTION_CONFIG[sectionType] || SECTION_CONFIG.tasks

  // Steps: 'user' -> 'select'
  const [step, setStep] = useState('user')

  // === USER STEP ===
  const [connections, setConnections] = useState([])
  const [connectionsLoading, setConnectionsLoading] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [selectedUserEmail, setSelectedUserEmail] = useState('')

  // === NEW CONNECTION SUB-FLOW ===
  const [showSendInput, setShowSendInput] = useState(false)
  const [sendEmail, setSendEmail] = useState('')
  const [sendError, setSendError] = useState('')
  const [sendLoading, setSendLoading] = useState(false)

  // === CATEGORY STEP ===
  const [catTree, setCatTree] = useState([])
  const [catLoading, setCatLoading] = useState(false)
  const [expandedCats, setExpandedCats] = useState(new Set())

  // Tasks loaded per cat/subcat via "Show" button
  const [visibleTasks, setVisibleTasks] = useState({}) // { catId: tasks[] }
  const [loadingTasksFor, setLoadingTasksFor] = useState(new Set())

  // Already shared task IDs with selected user
  const [alreadySharedIds, setAlreadySharedIds] = useState(new Set())

  // Checked (selected for sharing) task IDs — only new tasks, default all unchecked
  const [checkedIds, setCheckedIds] = useState(new Set())

  // Task count per category
  const [catCounts, setCatCounts] = useState({}) // { catId: number }

  // Success step
  const [successCount, setSuccessCount] = useState(0)

  const [saving, setSaving] = useState(false)

  // Reset everything on open
  useEffect(() => {
    if (show) {
      setStep('user')
      setConnections([])
      setSelectedUserId(null)
      setSelectedUserEmail('')
      setCatTree([])
      setExpandedCats(new Set())
      setVisibleTasks({})
      setLoadingTasksFor(new Set())
      setAlreadySharedIds(new Set())
      setCheckedIds(new Set())
      setCatCounts({})
      setSuccessCount(0)
      setSaving(false)
      setShowSendInput(false)
      setSendEmail('')
      setSendError('')
      setSendLoading(false)
      fetchConnections()
    }
  }, [show])

  // Fetch accepted connections
  const fetchConnections = async () => {
    setConnectionsLoading(true)
    try {
      const { data } = await supabase.from('profiles').select('share_connection').eq('id', UserId).single()
      const shareConn = data?.share_connection || {}
      const accepted = Object.entries(shareConn)
        .filter(([, e]) => e.accept && !e.cancel)
        .map(([id, e]) => ({ id, email: e.email }))
      setConnections(accepted)
    } catch (e) { console.error(e) }
    setConnectionsLoading(false)
  }

  // Send new connection request
  const handleSendRequest = async () => {
    const trimmedEmail = sendEmail.trim().toLowerCase()
    if (!trimmedEmail) { setSendError('Please enter an email address.'); return }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(trimmedEmail)) { setSendError('Please enter a valid email address.'); return }

    setSendLoading(true)
    setSendError('')

    try {
      const { data: receiverProfile, error: profileError } = await supabase
        .from('profiles').select('id, email').eq('email', trimmedEmail).maybeSingle()
      if (profileError) throw profileError
      if (!receiverProfile) { setSendError('No user found with this email address.'); setSendLoading(false); return }

      const receiverId = receiverProfile.id
      if (receiverId === UserId) { setSendError('You cannot send a request to yourself.'); setSendLoading(false); return }

      const { data: senderProfile } = await supabase
        .from('profiles').select('share_connection, email').eq('id', UserId).single()
      const senderConnections = senderProfile?.share_connection || {}
      const senderEmail = senderProfile?.email || ''

      if (senderConnections[receiverId] && !senderConnections[receiverId].cancel) { setSendError('Connection request already exists with this user.'); setSendLoading(false); return }

      const updatedSender = { ...senderConnections, [receiverId]: { email: trimmedEmail, is_sending: true, accept: false, cancel: false, message: '' } }
      const { error: sErr } = await supabase.from('profiles').update({ share_connection: updatedSender }).eq('id', UserId)
      if (sErr) throw sErr

      const { data: receiverData } = await supabase.from('profiles').select('share_connection').eq('id', receiverId).single()
      const receiverConnections = receiverData?.share_connection || {}
      const updatedReceiver = { ...receiverConnections, [UserId]: { email: senderEmail, is_sending: false, accept: false, cancel: false, message: '' } }
      const { error: rErr } = await supabase.from('profiles').update({ share_connection: updatedReceiver }).eq('id', receiverId)
      if (rErr) throw rErr

      setShowSendInput(false)
      setSendEmail('')
      fetchConnections()
    } catch (err) {
      console.error('Error sending connection request:', err)
      setSendError('Failed to send request. Please try again.')
    } finally {
      setSendLoading(false)
    }
  }

  // When user selects a connection → fetch already shared + category tree
  const handleUserSelect = async (userId, email) => {
    setSelectedUserId(userId)
    setSelectedUserEmail(email)
    setCatLoading(true)
    setStep('select')
    setVisibleTasks({})
    setCheckedIds(new Set())
    setExpandedCats(new Set())

    try {
      // Fetch already shared task IDs — with 10s timeout to avoid infinite spinner on slow mobile networks
      const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || 'http://localhost:3000'
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000)
      try {
        const statusRes = await fetch(`${API_URL}/api/sharing/tasks-copy-status?sender_id=${UserId}&receiver_id=${userId}&section_type=${sectionType}`, { signal: controller.signal })
        clearTimeout(timeoutId)
        const statusData = await statusRes.json()
        const sharedIds = new Set((statusData?.shared_task_ids || []).map(String))
        setAlreadySharedIds(sharedIds)
      } catch (fetchErr) {
        clearTimeout(timeoutId)
        console.error('ShareSectionPopup: status fetch failed, continuing with empty shared IDs:', fetchErr)
        setAlreadySharedIds(new Set())
      }

      // Fetch category tree (exclude shared categories)
      const { data: cats } = await supabase
        .from(config.catTable).select('id, name')
        .eq('user_id', UserId).is('parent_category_id', null)
        .or('is_share.is.null,is_share.eq.false')
        .order('name')

      const tree = []
      for (const cat of (cats || [])) {
        const { data: subs } = await supabase
          .from(config.catTable).select('id, name')
          .eq('user_id', UserId).eq('parent_category_id', cat.id)
          .or('is_share.is.null,is_share.eq.false')
          .order('name')
        tree.push({ ...cat, subcategories: subs || [] })
      }
      // Add "General" for uncategorized tasks
      tree.unshift({ id: 'general', name: 'General', subcategories: [] })
      setCatTree(tree)

      // Fetch task counts per category
      const allCatIds = []
      tree.forEach(cat => {
        if (cat.id !== 'general') {
          allCatIds.push(cat.id)
          cat.subcategories.forEach(sub => allCatIds.push(sub.id))
        }
      })

      const countMap = {}

      // Count for categorized tasks (exclude shared items)
      if (allCatIds.length > 0) {
        const { data: taskRows } = await supabase
          .from(config.table)
          .select(config.catField)
          .eq('user_id', UserId)
          .or('is_share.is.null,is_share.eq.false')
          .in(config.catField, allCatIds)

        ;(taskRows || []).forEach(row => {
          const cId = row[config.catField]
          countMap[cId] = (countMap[cId] || 0) + 1
        })
      }

      // Count for uncategorized (General) tasks (exclude shared items)
      // Use nested and() to combine both OR conditions in a single .or() call
      const { count: generalCount } = await supabase
        .from(config.table)
        .select('id', { count: 'exact', head: true })
        .eq('user_id', UserId)
        .or(`and(or(is_share.is.null,is_share.eq.false),or(${config.catField}.is.null,${config.catField}.eq.0))`)

      countMap['general'] = generalCount || 0
      setCatCounts(countMap)
    } catch (e) {
      console.error(e)
    } finally {
      setCatLoading(false)
    }
  }

  // Toggle expand parent category
  const toggleExpand = (catId) => {
    setExpandedCats(prev => {
      const next = new Set(prev)
      if (next.has(catId)) next.delete(catId); else next.add(catId)
      return next
    })
  }

  // Show/Hide tasks for a category or subcategory
  const handleShowTasks = async (catId) => {
    // Toggle off if already visible
    if (visibleTasks[catId]) {
      setVisibleTasks(prev => {
        const next = { ...prev }
        delete next[catId]
        return next
      })
      // Remove these tasks from checked
      const taskIds = (visibleTasks[catId] || []).map(t => t.id)
      setCheckedIds(prev => {
        const next = new Set(prev)
        taskIds.forEach(id => next.delete(id))
        return next
      })
      return
    }

    // Load tasks (default: all unchecked)
    setLoadingTasksFor(prev => new Set(prev).add(catId))
    try {
      const selectFields = config.hasDate
        ? `id, title, description, status, due_date, ${config.catField}`
        : `id, title, description, status, ${config.catField}`

      let query = supabase
        .from(config.table)
        .select(selectFields)
        .eq('user_id', UserId)
        .order('created_at', { ascending: false })

      if (catId === 'general') {
        // Combine is_share + catField filters in a single .or() to avoid override
        query = query.or(`and(or(is_share.is.null,is_share.eq.false),or(${config.catField}.is.null,${config.catField}.eq.0))`)
      } else {
        query = query.or('is_share.is.null,is_share.eq.false').eq(config.catField, catId)
      }

      const { data } = await query
      setVisibleTasks(prev => ({ ...prev, [catId]: data || [] }))
    } catch (e) { console.error(e) }

    setLoadingTasksFor(prev => {
      const next = new Set(prev)
      next.delete(catId)
      return next
    })
  }

  // Toggle individual task check
  const toggleTaskCheck = (taskId) => {
    if (alreadySharedIds.has(String(taskId))) return
    setCheckedIds(prev => {
      const next = new Set(prev)
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId)
      return next
    })
  }

  // Select/Deselect all new tasks for a single cat/subcat (used inside task list)
  const toggleAllForCat = (catId) => {
    const tasks = visibleTasks[catId] || []
    const newTasks = tasks.filter(t => !alreadySharedIds.has(String(t.id)))
    if (newTasks.length === 0) return

    const allChecked = newTasks.every(t => checkedIds.has(t.id))
    setCheckedIds(prev => {
      const next = new Set(prev)
      if (allChecked) {
        newTasks.forEach(t => next.delete(t.id))
      } else {
        newTasks.forEach(t => next.add(t.id))
      }
      return next
    })
  }

  // ====== CATEGORY CHECKBOX: load tasks if needed + toggle all ======

  const toggleCategoryCheck = async (catId, subcatIds = []) => {
    const allCatIds = [catId, ...subcatIds]
    const toLoad = allCatIds.filter(id => !visibleTasks[id])

    let loadedTasks = {}

    // Load tasks for categories that aren't loaded yet
    if (toLoad.length > 0) {
      setLoadingTasksFor(prev => {
        const next = new Set(prev)
        toLoad.forEach(id => next.add(id))
        return next
      })

      try {
        const selectFields = config.hasDate
          ? `id, title, description, status, due_date, ${config.catField}`
          : `id, title, description, status, ${config.catField}`

        const hasGeneral = toLoad.includes('general')
        const normalIds = toLoad.filter(id => id !== 'general')

        toLoad.forEach(id => { loadedTasks[id] = [] })

        // Fetch categorized tasks (exclude shared items)
        if (normalIds.length > 0) {
          const { data } = await supabase
            .from(config.table)
            .select(selectFields)
            .eq('user_id', UserId)
            .or('is_share.is.null,is_share.eq.false')
            .in(config.catField, normalIds)
            .order('created_at', { ascending: false })

          ;(data || []).forEach(task => {
            const cId = task[config.catField]
            if (loadedTasks[cId]) loadedTasks[cId].push(task)
          })
        }

        // Fetch uncategorized (General) tasks (exclude shared items)
        if (hasGeneral) {
          const { data: generalData } = await supabase
            .from(config.table)
            .select(selectFields)
            .eq('user_id', UserId)
            .or(`and(or(is_share.is.null,is_share.eq.false),or(${config.catField}.is.null,${config.catField}.eq.0))`)
            .order('created_at', { ascending: false })

          loadedTasks['general'] = generalData || []
        }

        setVisibleTasks(prev => ({ ...prev, ...loadedTasks }))
      } catch (e) { console.error(e) }

      setLoadingTasksFor(prev => {
        const next = new Set(prev)
        toLoad.forEach(id => next.delete(id))
        return next
      })
    }

    // Collect all new task IDs (from freshly loaded + already visible)
    const allNewTaskIds = []
    allCatIds.forEach(id => {
      const tasks = loadedTasks[id] || visibleTasks[id] || []
      tasks.forEach(t => {
        if (!alreadySharedIds.has(String(t.id))) allNewTaskIds.push(t.id)
      })
    })

    if (allNewTaskIds.length === 0) return

    // Toggle: if all checked → uncheck all, else check all
    const allChecked = allNewTaskIds.every(id => checkedIds.has(id))
    setCheckedIds(prev => {
      const next = new Set(prev)
      if (allChecked) {
        allNewTaskIds.forEach(id => next.delete(id))
      } else {
        allNewTaskIds.forEach(id => next.add(id))
      }
      return next
    })

    // Auto-expand parent so subcategories are visible
    if (subcatIds.length > 0) {
      setExpandedCats(prev => {
        const next = new Set(prev)
        next.add(catId)
        return next
      })
    }
  }

  // Get checkbox state for a category (checked / indeterminate)
  const getCatCheckState = (catId, subcatIds = []) => {
    const allCatIds = [catId, ...subcatIds]
    let totalNew = 0
    let checkedNew = 0

    allCatIds.forEach(id => {
      const tasks = visibleTasks[id]
      if (!tasks) return
      tasks.forEach(t => {
        if (!alreadySharedIds.has(String(t.id))) {
          totalNew++
          if (checkedIds.has(t.id)) checkedNew++
        }
      })
    })

    return {
      checked: totalNew > 0 && checkedNew === totalNew,
      indeterminate: checkedNew > 0 && checkedNew < totalNew,
    }
  }

  // Get total task count for a category (including subcategories)
  const getTotalCount = (catId, subcatIds = []) => {
    let total = catCounts[catId] || 0
    subcatIds.forEach(id => { total += catCounts[id] || 0 })
    return total
  }

  const newCheckedCount = checkedIds.size

  // Share handler - copies categories + tasks to receiver's account via API
  const handleShare = async () => {
    const taskIds = [...checkedIds]
    if (taskIds.length === 0) return

    setSaving(true)
    try {
      // Get sender email from profiles
      const { data: senderProfile } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', UserId)
        .single()

      const senderEmail = senderProfile?.email || ''

      const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || 'http://localhost:3000'

      const res = await fetch(`${API_URL}/api/sharing/tasks-copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_id: UserId,
          sender_email: senderEmail,
          receiver_id: selectedUserId,
          task_ids: taskIds,
          section_type: sectionType,
        }),
      })

      const result = await res.json()
      if (!res.ok) throw new Error(result.error || 'Failed to share')

      setSuccessCount(taskIds.length)
      setSaving(false)
      setStep('success')
      setTimeout(() => { handleClose() }, 2000)
      return
    } catch (e) {
      console.error('Error sharing content:', e)
    }
    setSaving(false)
  }

  // ====== RENDER HELPERS ======

  const renderTask = (task) => {
    const isShared = alreadySharedIds.has(String(task.id))
    const isChecked = isShared || checkedIds.has(task.id)
    const isPending = task.status !== 'completed'

    return (
      <div
        key={task.id}
        className="d-flex align-items-start"
        onClick={() => !isShared && toggleTaskCheck(task.id)}
        style={{
          padding: '8px 12px',
          marginBottom: '4px',
          borderRadius: '8px',
          background: isShared
            ? (isDarkMode ? 'rgba(148,163,184,0.06)' : 'rgba(100,116,139,0.04)')
            : 'transparent',
          opacity: isShared ? 0.55 : 1,
          cursor: isShared ? 'not-allowed' : 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={isChecked}
          disabled={isShared}
          onChange={() => {}}
          style={{
            accentColor: isShared ? '#94a3b8' : 'var(--teal-bg)',
            width: '15px', height: '15px',
            cursor: isShared ? 'not-allowed' : 'pointer',
            marginRight: '10px', marginTop: '3px', flexShrink: 0
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="d-flex align-items-center gap-2">
            <span style={{
              fontSize: '0.82rem', fontWeight: 500, color: 'var(--text)',
              textDecoration: !isPending ? 'line-through' : 'none',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {task.title}
            </span>
            {isShared && (
              <span style={{
                fontSize: '0.6rem', fontWeight: 600,
                background: isDarkMode ? 'rgba(148,163,184,0.15)' : 'rgba(100,116,139,0.1)',
                color: isDarkMode ? '#94a3b8' : '#64748b',
                padding: '1px 6px', borderRadius: '4px', whiteSpace: 'nowrap',
              }}>
                Already shared
              </span>
            )}
          </div>
          {task.description && (
            <div style={{
              fontSize: '0.72rem', color: isDarkMode ? '#94a3b8' : '#64748b',
              marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
            }}>
              <span dangerouslySetInnerHTML={{ __html: task.description?.substring(0, 150) || '' }} />
            </div>
          )}
          <div className="d-flex align-items-center gap-2 mt-1">
            {isPending
              ? <span className="message-success-label fw-normal" style={{ backgroundColor: 'var(--orange-bg)', padding: '2px 10px' }}>Pending</span>
              : <span className="message-success-label fw-normal" style={{ padding: '2px 10px' }}>Completed</span>
            }
            {config.hasDate && task.due_date && (
              <span className="mb-0 fs-6 due_task_date" style={{ fontSize: '0.65rem', color: '#f59e0b' }}>
                <i className="bi bi-calendar-event me-1"></i>{DateFormtter(task.due_date)}
              </span>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderShowButton = (catId) => {
    const isVisible = !!visibleTasks[catId]
    const isLoading = loadingTasksFor.has(catId)

    return (
      <button
        className="btn py-0 px-2"
        onClick={(e) => { e.stopPropagation(); handleShowTasks(catId) }}
        disabled={isLoading}
        style={{
          fontSize: '0.7rem', fontWeight: 500,
          background: isVisible ? 'rgba(var(--teal-bg-rag), 0.1)' : 'transparent',
          color: 'var(--teal-bg)',
          border: '1px solid var(--teal-bg)',
          borderRadius: '6px', lineHeight: '20px',
          whiteSpace: 'nowrap',
        }}
      >
        {isLoading ? (
          <Spinner as="span" animation="border" size="sm" style={{ width: '10px', height: '10px' }} />
        ) : isVisible ? (
          <><i className="bi bi-eye-slash me-1"></i>Hide</>
        ) : (
          <><i className="bi bi-eye me-1"></i>Show</>
        )}
      </button>
    )
  }

  const renderTasksForCat = (catId) => {
    if (!visibleTasks[catId]) return null
    const tasks = visibleTasks[catId]
    const newTasks = tasks.filter(t => !alreadySharedIds.has(String(t.id)))
    const sharedTasks = tasks.filter(t => alreadySharedIds.has(String(t.id)))

    return (
      <div style={{
        marginTop: '6px', marginLeft: '12px',
        borderLeft: '2px solid var(--border-color)',
        paddingLeft: '8px',
      }}>
        {tasks.length === 0 ? (
          <div style={{ fontSize: '0.75rem', color: 'var(--text)', opacity: 0.4, padding: '8px', textAlign: 'center' }}>
            No items in this category
          </div>
        ) : (
          <>
            <div className="d-flex align-items-center justify-content-between" style={{ padding: '4px 12px' }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--text)', opacity: 0.6 }}>
                {newTasks.length} new · {sharedTasks.length} already shared
              </span>
            </div>
            {tasks.map(task => renderTask(task))}
          </>
        )}
      </div>
    )
  }

  // Render category checkbox
  const renderCatCheckbox = (catId, subcatIds = []) => {
    const { checked, indeterminate } = getCatCheckState(catId, subcatIds)
    const isLoading = [catId, ...subcatIds].some(id => loadingTasksFor.has(id))

    if (isLoading) {
      return (
        <Spinner
          as="span" animation="border" size="sm"
          style={{ width: '14px', height: '14px', marginRight: '10px', flexShrink: 0, color: 'var(--teal-bg)' }}
        />
      )
    }

    return (
      <input
        type="checkbox"
        ref={el => { if (el) el.indeterminate = indeterminate }}
        checked={checked}
        onChange={() => toggleCategoryCheck(catId, subcatIds)}
        style={{
          accentColor: 'var(--teal-bg)',
          width: '16px', height: '16px',
          cursor: 'pointer',
          marginRight: '10px', flexShrink: 0,
        }}
      />
    )
  }

  return (
    <Modal show={show} onHide={step === 'success' ? () => {} : handleClose} size={step === 'success' ? 'sm' : 'xl'} className="task-popup export-popup" centered backdrop={step === 'success' ? 'static' : true}>
      <Modal.Header closeButton={step !== 'success'}>
        <Modal.Title>
          {step === 'user' ? 'Select User' : step === 'success' ? 'Shared!' : (
            <>
              {config.title}
              {selectedUserEmail && (
                <span style={{ fontSize: '0.75rem', fontWeight: 400, marginLeft: '10px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                  → {selectedUserEmail}
                </span>
              )}
              {newCheckedCount > 0 && (
                <span style={{ fontSize: '0.75rem', fontWeight: 400, marginLeft: '12px', color: 'var(--teal-bg)' }}>
                  {newCheckedCount} selected
                </span>
              )}
            </>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="export-more" style={{ overflow: 'visible', display: 'flex', flexDirection: 'column', height: step === 'success' ? 'auto' : '70vh', padding: '16px' }}>
        {step === 'success' ? (
          /* ====== SUCCESS ====== */
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              background: 'rgba(var(--teal-bg-rag), 0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: '16px',
            }}>
              <i className="bi bi-check-lg" style={{ fontSize: '32px', color: 'var(--teal-bg)' }}></i>
            </div>
            <p style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)', margin: '0 0 6px' }}>
              Shared successfully!
            </p>
            <p style={{ fontSize: '0.82rem', color: isDarkMode ? '#94a3b8' : '#64748b', margin: 0 }}>
              {successCount} {sectionType === 'tasks' ? 'task' : sectionType === 'notes' ? 'note' : sectionType === 'lists' ? 'list' : 'item'}{successCount !== 1 ? 's' : ''} shared with {selectedUserEmail}
            </p>
          </div>
        ) : step === 'user' ? (
          /* ====== STEP 1: Select User ====== */
          connectionsLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : showSendInput ? (
            /* ---- New Connection: email input view ---- */
            <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
              <i className="bi bi-person-plus" style={{ fontSize: '36px', color: 'var(--teal-bg)', marginBottom: '12px' }}></i>
              <p style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>New Connection</p>
              <p style={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : '#64748b', marginBottom: '16px' }}>
                Enter the email of the user you want to connect with
              </p>
              <div style={{ width: '100%', maxWidth: '360px' }}>
                <input
                  type="email"
                  className="form-control"
                  placeholder="user@example.com"
                  value={sendEmail}
                  onChange={(e) => { setSendEmail(e.target.value); setSendError('') }}
                  autoComplete="off"
                  disabled={sendLoading}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSendRequest() }}
                  style={{ fontSize: '0.85rem', borderRadius: '8px', padding: '8px 12px' }}
                />
                {sendError && <small className="text-danger d-block mt-1">{sendError}</small>}
                <div className="d-flex gap-2 mt-3 justify-content-center">
                  <Button
                    variant="secondary" size="sm"
                    onClick={() => { setShowSendInput(false); setSendEmail(''); setSendError('') }}
                    disabled={sendLoading}
                  >
                    <i className="bi bi-arrow-left me-1"></i>Back
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSendRequest}
                    disabled={sendLoading || !sendEmail.trim()}
                    style={{ background: 'var(--teal-bg)', border: 'none' }}
                  >
                    {sendLoading ? <Spinner as="span" animation="border" size="sm" className="me-1" /> : <i className="bi bi-send me-1"></i>}
                    Send
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ flexGrow: 1, overflowY: 'auto' }}>
              <label style={{ fontSize: '0.8rem', color: 'var(--text)', marginBottom: '12px', display: 'block', opacity: 0.7 }}>
                Select a user to share {sectionType} with:
              </label>
              {connections.map(conn => (
                <div
                  key={conn.id}
                  className="d-flex align-items-center gap-3"
                  onClick={() => handleUserSelect(conn.id, conn.email)}
                  style={{
                    padding: '12px 16px', marginBottom: '8px', borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--background)',
                    cursor: 'pointer',
                  }}
                >
                  <i className="bi bi-person-circle" style={{ fontSize: '22px', color: 'var(--teal-bg)' }}></i>
                  <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text)' }}>{conn.email}</span>
                  <i className="bi bi-chevron-right ms-auto" style={{ fontSize: '0.75rem', color: 'var(--text)', opacity: 0.4 }}></i>
                </div>
              ))}
              {/* New Connection button */}
              <div
                className="d-flex align-items-center gap-3"
                onClick={() => setShowSendInput(true)}
                style={{
                  padding: '12px 16px', marginBottom: '8px', borderRadius: '12px',
                  border: '1.5px dashed var(--teal-bg)',
                  background: 'transparent',
                  cursor: 'pointer',
                  opacity: 0.85,
                }}
              >
                <i className="bi bi-person-plus" style={{ fontSize: '22px', color: 'var(--teal-bg)' }}></i>
                <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--teal-bg)' }}>New Connection</span>
              </div>
            </div>
          )
        ) : (
          /* ====== STEP 2: Categories + Tasks ====== */
          catLoading ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5" style={{ flexGrow: 1 }}>
              <Spinner animation="border" style={{ color: 'var(--teal-bg)', width: '2.5rem', height: '2.5rem' }} />
              <p style={{ fontSize: '0.85rem', color: 'var(--text)', opacity: 0.6, marginTop: '14px', marginBottom: 0 }}>
                Loading categories for {selectedUserEmail}...
              </p>
            </div>
          ) : catTree.length === 0 ? (
            <div className="text-center py-4" style={{ color: 'var(--text)', opacity: 0.5, fontSize: '0.85rem' }}>No categories found</div>
          ) : (
            <div className="modal-scroll p-2" style={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
              {catTree.map(cat => {
                const subcatIds = cat.subcategories.map(s => s.id)

                return (
                  <div key={cat.id} style={{ marginBottom: '8px' }}>
                    {/* Parent category row */}
                    <div
                      className="d-flex align-items-center"
                      style={{
                        padding: '10px 14px',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--background)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      }}
                    >
                      {cat.subcategories.length > 0 ? (
                        <i
                          className={`bi bi-chevron-${expandedCats.has(cat.id) ? 'down' : 'right'}`}
                          onClick={() => toggleExpand(cat.id)}
                          style={{ fontSize: '0.75rem', color: 'var(--text)', opacity: 0.5, cursor: 'pointer', marginRight: '10px' }}
                        ></i>
                      ) : (
                        <span style={{ width: '18px', marginRight: '10px' }}></span>
                      )}
                      {renderCatCheckbox(cat.id, subcatIds)}
                      <i className="bi bi-folder2" style={{ fontSize: '1rem', color: 'var(--teal-bg)', marginRight: '10px' }}></i>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>{cat.name}</span>
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 600, marginLeft: '6px',
                        background: isDarkMode ? 'rgba(var(--teal-bg-rag), 0.15)' : 'rgba(var(--teal-bg-rag), 0.1)',
                        color: 'var(--teal-bg)', padding: '1px 7px', borderRadius: '10px',
                      }}>
                        {getTotalCount(cat.id, subcatIds)}
                      </span>
                      <span style={{ flex: 1 }}></span>
                      {cat.subcategories.length > 0 && (
                        <span style={{ fontSize: '0.65rem', color: 'var(--text)', opacity: 0.4, marginRight: '10px' }}>{cat.subcategories.length} sub</span>
                      )}
                      {renderShowButton(cat.id)}
                    </div>

                    {/* Tasks for parent category (when Show clicked) */}
                    {renderTasksForCat(cat.id)}

                    {/* Subcategories */}
                    {cat.subcategories.length > 0 && expandedCats.has(cat.id) && (
                      <div style={{ paddingLeft: '28px' }}>
                        {cat.subcategories.map(sub => (
                          <div key={sub.id} style={{ marginTop: '6px' }}>
                            <div
                              className="d-flex align-items-center"
                              style={{
                                padding: '8px 12px',
                                borderRadius: '10px',
                                border: '1px solid var(--border-color)',
                                background: 'var(--background)',
                              }}
                            >
                              {renderCatCheckbox(sub.id)}
                              <i className="bi bi-folder" style={{ fontSize: '0.9rem', color: 'var(--text)', opacity: 0.6, marginRight: '8px' }}></i>
                              <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text)' }}>{sub.name}</span>
                              <span style={{
                                fontSize: '0.6rem', fontWeight: 600, marginLeft: '6px',
                                background: isDarkMode ? 'rgba(var(--teal-bg-rag), 0.15)' : 'rgba(var(--teal-bg-rag), 0.1)',
                                color: 'var(--teal-bg)', padding: '1px 6px', borderRadius: '10px',
                              }}>
                                {catCounts[sub.id] || 0}
                              </span>
                              <span style={{ flex: 1 }}></span>
                              {renderShowButton(sub.id)}
                            </div>
                            {/* Tasks for subcategory (when Show clicked) */}
                            {renderTasksForCat(sub.id)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        )}
      </Modal.Body>

      {step !== 'success' && (
        <Modal.Footer>
          {step === 'user' ? (
            <Button variant="secondary" size="sm" onClick={handleClose}>Cancel</Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={() => {
                setStep('user')
                setVisibleTasks({})
                setCheckedIds(new Set())
                setExpandedCats(new Set())
              }}>
                <i className="bi bi-arrow-left me-1"></i> Back
              </Button>
              <Button
                size="sm"
                onClick={handleShare}
                disabled={saving || newCheckedCount === 0}
                style={{ background: 'var(--teal-bg)', border: 'none' }}
              >
                {saving ? <Spinner as="span" animation="border" size="sm" className="me-1" /> : <i className="bi bi-share me-1"></i>}
                Share ({newCheckedCount})
              </Button>
            </>
          )}
        </Modal.Footer>
      )}
    </Modal>
  )
}

export default ShareSectionPopup
